import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { FileText, Plus, Loader2, Trash2, Archive, Eye, Clock, CheckCircle, AlertCircle, Shield } from 'lucide-react'
import { getReports, generateReport, deleteReport, archiveReport } from '../../services/api'

const STATUS_CONFIG = {
  generating: { label: 'Generating', color: 'bg-blue-100 text-blue-800', icon: Loader2, animate: true },
  draft: { label: 'Draft', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  published: { label: 'Published', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-600', icon: Archive },
  error: { label: 'Error', color: 'bg-red-100 text-red-800', icon: AlertCircle },
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft
  const Icon = config.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className={`h-3.5 w-3.5 ${config.animate ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  )
}

export default function ReportList({ ranchId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState(null)
  const [showGenerateDialog, setShowGenerateDialog] = useState(false)

  // Default period: last 30 days (the detailed report is the monthly
  // manager deep-dive; owners get the weekly Tuesday Report).
  const today = new Date()
  const monthAgo = new Date(today)
  monthAgo.setDate(monthAgo.getDate() - 30)
  const [periodStart, setPeriodStart] = useState(monthAgo.toISOString().split('T')[0])
  const [periodEnd, setPeriodEnd] = useState(today.toISOString().split('T')[0])
  const [reportType, setReportType] = useState('biweekly')

  const { data, isLoading } = useQuery({
    queryKey: ['reports', ranchId, statusFilter],
    queryFn: () => getReports(ranchId, { status: statusFilter }),
    refetchInterval: 10000, // Poll for generating status
  })

  const generateMutation = useMutation({
    mutationFn: (params) => generateReport(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      setShowGenerateDialog(false)
      navigate(`/reports/${data.id}`)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteReport(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports'] }),
  })

  const archiveMutation = useMutation({
    mutationFn: (id) => archiveReport(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reports'] }),
  })

  const reports = data?.items || []
  const filterTabs = [
    { label: 'All', value: null },
    { label: 'Drafts', value: 'draft' },
    { label: 'Published', value: 'published' },
    { label: 'Archived', value: 'archived' },
  ]

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ranch Reports</h1>
          <p className="text-sm text-earth-500 mt-1">AI-generated reports analyzing your ranch performance</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/reports/weekly"
            className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2.5 rounded-lg hover:bg-green-100 transition-colors font-medium"
          >
            Weekly Report
          </Link>
          <Link
            to="/reports/evidence-ledger"
            className="inline-flex items-center gap-2 bg-purple-50 text-purple-700 px-4 py-2.5 rounded-lg hover:bg-purple-100 transition-colors font-medium"
          >
            <Shield className="h-5 w-5" />
            Evidence Ledger
          </Link>
          <button
            onClick={() => setShowGenerateDialog(true)}
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2.5 rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-sm"
          >
            <Plus className="h-5 w-5" />
            Generate Report
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 mb-6 bg-earth-100 p-1 rounded-lg w-fit">
        {filterTabs.map((tab) => (
          <button
            key={tab.label}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === tab.value
                ? 'bg-white text-earth-900 shadow-sm'
                : 'text-earth-600 hover:text-earth-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Report List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-earth-200">
          <FileText className="h-12 w-12 text-earth-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-earth-700 mb-2">No reports yet</h3>
          <p className="text-earth-500 mb-6">Generate your first AI-powered ranch report</p>
          <button
            onClick={() => setShowGenerateDialog(true)}
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2.5 rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            <Plus className="h-5 w-5" />
            Generate Report
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <div
              key={report.id}
              className="bg-white rounded-lg border border-earth-200 p-4 hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
              onClick={() => navigate(`/reports/${report.id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FileText className="h-5 w-5 text-earth-400 shrink-0" />
                  <div className="min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{report.title}</h3>
                    <p className="text-sm text-earth-500">
                      {new Date(report.period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      {' - '}
                      {new Date(report.period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <StatusBadge status={report.status} />
                  <span className="text-xs text-earth-400">
                    {new Date(report.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => navigate(`/reports/${report.id}`)}
                      className="p-1.5 rounded hover:bg-earth-100 text-earth-500 hover:text-earth-700"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {report.status !== 'archived' && (
                      <button
                        onClick={() => archiveMutation.mutate(report.id)}
                        className="p-1.5 rounded hover:bg-earth-100 text-earth-500 hover:text-earth-700"
                        title="Archive"
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (confirm('Delete this report?')) deleteMutation.mutate(report.id)
                      }}
                      className="p-1.5 rounded hover:bg-red-50 text-earth-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Generate Report Dialog */}
      {showGenerateDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-900/60" onClick={() => setShowGenerateDialog(false)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Ranch Report</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">Report Type</label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full px-3 py-2 border border-earth-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom Period</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={periodStart}
                    onChange={(e) => setPeriodStart(e.target.value)}
                    className="w-full px-3 py-2 border border-earth-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={periodEnd}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                    className="w-full px-3 py-2 border border-earth-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowGenerateDialog(false)}
                className="px-4 py-2 text-sm font-medium text-earth-700 hover:bg-earth-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => generateMutation.mutate({
                  ranch_id: ranchId,
                  period_start: periodStart,
                  period_end: periodEnd,
                  report_type: reportType,
                })}
                disabled={generateMutation.isPending}
                className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
              >
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
